/**
 * Usage Reports Service
 * API service for fetching usage reports data
 */
import { Injectable, inject } from '@angular/core';
import { HttpClient, HttpHeaders, HttpParams } from '@angular/common/http';
import { Observable, of } from 'rxjs';
import { catchError, map, switchMap } from 'rxjs/operators';
import { environment } from '../../../../environments/environment';
import { UsageStats, DailyUsage, ProcessingLog, UsageReportFilters } from '../models/usage-reports.models';
import { formatDateTime } from '@shared/utils/date-time.util';

export const USAGE_EXPORT_ERROR = 'Unable to generate the report. Please try again.';

const SKIP_ERROR_HEADERS = new HttpHeaders().set('X-Skip-Error-Notification', 'true');

const STATUS_TO_BACKEND: Record<string, string> = {
  success: 'completed'
};

const STATUS_FROM_BACKEND: Record<string, ProcessingLog['status']> = {
  completed: 'success',
  success: 'success',
  failed: 'failed',
  processing: 'processing',
  queued: 'processing',
  pending: 'processing'
};

interface UsageStatsApi {
  documents_processed?: number;
  success_rate?: number;
  credits_used?: number;
  total_credits_used?: number;
  api_calls?: number;
  total_cost?: number;
  documents_change?: number;
  success_rate_change?: number;
  credits_change?: number;
  cost_change?: number;
}

interface DailyUsageApi {
  date?: string;
  documents?: number;
  successful?: number;
  failed?: number;
  api_calls?: number;
  pages?: number;
  credits?: number;
  count?: number;
  label?: string;
}

interface ProcessingLogApi {
  id?: string;
  document_name?: string;
  documentName?: string;
  template_name?: string;
  template?: string;
  status?: string;
  parsing_status?: string;
  processing_time?: number | string;
  processing_time_ms?: number;
  processingTime?: string;
  pages?: number;
  credits?: number;
  credits_used?: number;
  created_at?: string;
  dateTime?: string;
}

function pickCreditsUsed(data: UsageStatsApi): number | undefined {
  if (typeof data.credits_used === 'number') {
    return data.credits_used;
  }
  if (typeof data.total_credits_used === 'number') {
    return data.total_credits_used;
  }
  return undefined;
}

export function mapUsageStats(data: UsageStatsApi): UsageStats {
  const creditsUsed = pickCreditsUsed(data);
  return {
    documentsProcessed: data.documents_processed ?? 0,
    successRate: data.success_rate ?? 0,
    creditsUsed: creditsUsed ?? 0,
    totalCost: data.total_cost ?? 0,
    documentsChange: typeof data.documents_change === 'number' ? data.documents_change : null,
    successRateChange: typeof data.success_rate_change === 'number' ? data.success_rate_change : null,
    creditsChange: typeof data.credits_change === 'number' ? data.credits_change : null,
    costChange: typeof data.cost_change === 'number' ? data.cost_change : null
  };
}

export function mapDailyUsage(rows: DailyUsageApi[]): DailyUsage[] {
  return (rows || []).map((row) => {
    const date = row.date || '';
    const parsed = date ? new Date(date) : null;
    const label = row.label
      || (parsed && !isNaN(parsed.getTime())
        ? parsed.toLocaleDateString(undefined, { month: 'short', day: 'numeric' })
        : date);
    return {
      date,
      count: row.documents ?? row.count ?? 0,
      label
    };
  });
}

function formatProcessingTime(value: number | string | undefined, ms?: number): string {
  if (typeof value === 'string' && value.trim()) {
    return value;
  }
  const seconds = typeof value === 'number'
    ? value
    : (typeof ms === 'number' ? ms / 1000 : 0);
  if (!seconds) {
    return '0s';
  }
  return `${Number(seconds).toFixed(seconds >= 10 ? 0 : 1)}s`;
}

export function mapProcessingLog(raw: ProcessingLogApi): ProcessingLog {
  const statusRaw = (raw.status || raw.parsing_status || 'processing').toLowerCase();
  return {
    id: raw.id || '',
    dateTime: formatDateTime(raw.dateTime || raw.created_at),
    documentName: raw.documentName || raw.document_name || 'Unknown',
    template: raw.template || raw.template_name || 'No Template',
    pages: raw.pages ?? 0,
    credits: raw.credits_used ?? raw.credits ?? 0,
    status: STATUS_FROM_BACKEND[statusRaw] || 'processing',
    processingTime: formatProcessingTime(raw.processingTime ?? raw.processing_time, raw.processing_time_ms)
  };
}

export function toBackendStatus(status: string): string {
  if (status === 'all') {
    return status;
  }
  return STATUS_TO_BACKEND[status] || status;
}

export function dateRangeToBounds(dateRange: string): { start: Date; end: Date } {
  const end = new Date();
  const start = new Date();
  switch (dateRange) {
    case 'last7':
      start.setDate(end.getDate() - 7);
      break;
    case 'last90':
      start.setDate(end.getDate() - 90);
      break;
    case 'thisMonth':
      start.setFullYear(end.getFullYear(), end.getMonth(), 1);
      start.setHours(0, 0, 0, 0);
      break;
    case 'lastMonth': {
      start.setFullYear(end.getFullYear(), end.getMonth() - 1, 1);
      start.setHours(0, 0, 0, 0);
      break;
    }
    case 'last30':
    default:
      start.setDate(end.getDate() - 30);
      break;
  }
  return { start, end };
}

@Injectable({
  providedIn: 'root'
})
export class UsageReportsService {
  private http = inject(HttpClient);
  private apiUrl = environment.apiUrl;

  getUsageStats(dateRange: string): Observable<UsageStats> {
    const params = new HttpParams().set('date_range', dateRange);
    return this.http.get<UsageStatsApi>(`${this.apiUrl}/analytics/usage-stats`, { params }).pipe(
      switchMap((data) => {
        const stats = mapUsageStats(data);
        if (pickCreditsUsed(data) !== undefined) {
          return of(stats);
        }
        return this.resolveCreditsUsed(dateRange).pipe(
          map((creditsUsed) => ({ ...stats, creditsUsed })),
          catchError(() => of(stats))
        );
      })
    );
  }

  getDailyUsage(dateRange: string): Observable<DailyUsage[]> {
    const params = new HttpParams().set('date_range', dateRange);
    return this.http.get<DailyUsageApi[]>(`${this.apiUrl}/analytics/daily-usage`, {
      params,
      headers: SKIP_ERROR_HEADERS
    }).pipe(
      map((rows) => mapDailyUsage(rows || [])),
      catchError(() => of([]))
    );
  }

  getProcessingLogs(
    filters: UsageReportFilters,
    page: number,
    pageSize: number
  ): Observable<{ logs: ProcessingLog[]; total: number }> {
    let params = new HttpParams()
      .set('page', page.toString())
      .set('page_size', pageSize.toString())
      .set('date_range', filters.dateRange);

    if (filters.template !== 'all') {
      params = params.set('template', filters.template);
    }
    if (filters.status !== 'all') {
      params = params.set('status', toBackendStatus(filters.status));
    }
    if (filters.search) {
      params = params.set('search', filters.search);
    }

    return this.http.get<{ logs: ProcessingLogApi[]; total: number }>(
      `${this.apiUrl}/analytics/processing-logs`,
      { params }
    ).pipe(
      map((response) => ({
        logs: (response.logs || []).map(mapProcessingLog),
        total: response.total ?? 0
      }))
    );
  }

  exportReport(format: 'csv' | 'pdf', filters: UsageReportFilters): Observable<Blob> {
    const { start, end } = dateRangeToBounds(filters.dateRange);
    let params = new HttpParams()
      .set('report_type', 'documents')
      .set('format', format)
      .set('start_date', start.toISOString())
      .set('end_date', end.toISOString());

    if (filters.template && filters.template !== 'all') {
      params = params.set('template_id', filters.template);
    }
    if (filters.status && filters.status !== 'all') {
      params = params.set('status', filters.status);
    }
    if (filters.search) {
      params = params.set('search', filters.search);
    }

    return this.http.post(`${this.apiUrl}/analytics/export`, {}, {
      params,
      responseType: 'blob',
      headers: SKIP_ERROR_HEADERS
    });
  }

  private resolveCreditsUsed(dateRange: string): Observable<number> {
    const period = dateRange === 'last7' ? 'week' : 'month';
    const params = new HttpParams().set('period', period);
    return this.http.get<{ data?: { total_credits_used?: number } }>(
      `${this.apiUrl}/analytics/credits/usage`,
      { params, headers: SKIP_ERROR_HEADERS }
    ).pipe(
      switchMap((response) => {
        const fromUsage = response?.data?.total_credits_used;
        if (typeof fromUsage === 'number') {
          return of(fromUsage);
        }
        return this.http.get<{ credits_used?: number }>(
          `${this.apiUrl}/billing/credits/balance`,
          { headers: SKIP_ERROR_HEADERS }
        ).pipe(
          map((balance) => balance.credits_used ?? 0)
        );
      })
    );
  }
}
